// utils/ResourceScopeBadge.jsx
//
// The single visual primitive for the opt-in attachment model.
//
// Renders one of five badge states, matching the SCOPE_BADGE constants
// in ./scopeAttachmentValidation.js. Every screen that shows a
// tenant-scoped resource in a list, dropdown, or card header renders
// this next to it, so the operator can tell at a glance:
//
//   • whether the resource is app-scoped or tenant-scoped
//   • whether the current app has attached it
//   • (in the global admin view) whether any app has attached it
//
// Adding a new state means: add it to SCOPE_BADGE in
// scopeAttachmentValidation.js, and it lands here automatically. The
// component is intentionally dumb about the five states — the
// classification logic lives in scopeAttachmentValidation.js so
// multiple screens produce the same state name for the same row.
//
// The component is presentational. It never fetches, never writes,
// never mutates. The caller decides when to render it and what
// classification to pass.

import React from "react";
import { SCOPE_BADGE } from "./scopeAttachmentValidation";

/**
 * Props:
 *
 *   kind    — "app"
 *           | "tenant-attached"
 *           | "tenant-available"
 *           | "tenant-detached-blocked"
 *           | "unknown"
 *             (missing → "unknown"; the badge is cosmetic and never
 *              the reason a screen fails to render)
 *
 *   size    — "sm" | "md" (default "md")
 *
 *   tooltip — optional string; overrides the default title from
 *             SCOPE_BADGE. Screens use this to add context that isn't
 *             in the badge label — e.g. "Attached on 2026-10-15".
 *
 *   onClick — optional; if provided, the badge becomes a button.
 *             The click is wrapped in e.stopPropagation() so a badge
 *             inside a clickable row doesn't open the row's detail
 *             pane when the operator wanted to click the badge.
 *             In the global-admin view this is used to open the
 *             "Used by" chip stack; in the app view it is used to
 *             open the "Detach" confirm on tenant-attached rows.
 *
 *   count   — optional number. When provided, appended to the label
 *             as a count chip — e.g. "🔗 3 apps". Only meaningful
 *             for the "tenant-detached-blocked" state in the global
 *             admin view; harmless elsewhere.
 */
const ResourceScopeBadge = ({
  kind = "unknown",
  size = "md",
  tooltip,
  onClick,
  count,
}) => {
  const meta = SCOPE_BADGE[kind] || SCOPE_BADGE.unknown;

  const isSmall = size === "sm";

  const style = {
    display: "inline-flex",
    alignItems: "center",
    gap: isSmall ? "3px" : "4px",
    padding: isSmall ? "1px 7px" : "2px 9px",
    fontSize: isSmall ? "10px" : "11px",
    fontWeight: 600,
    borderRadius: 10,
    lineHeight: 1.4,
    color: meta.color,
    backgroundColor: meta.bg,
    border: `1px solid ${meta.border}`,
    cursor: onClick ? "pointer" : "default",
    userSelect: "none",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    transition: "background-color 0.15s ease",
  };

  const title = tooltip || meta.title;

  const label = (
    <>
      {meta.label}
      {typeof count === "number" && count > 0 && (
        <span
          style={{
            marginLeft: 2,
            padding: "0 4px",
            borderRadius: 6,
            background: "rgba(255,255,255,0.6)",
            fontSize: isSmall ? "9px" : "10px",
            fontWeight: 700,
          }}
        >
          {count}
        </span>
      )}
    </>
  );

  // Non-clickable: render as a <span> so it nests cleanly inside
  // other clickable parents (dropdown option labels, card headers).
  if (!onClick) {
    return (
      <span style={style} title={title}>
        {label}
      </span>
    );
  }

  // Clickable: render as a <button>. The click handler is the caller's
  // decision — typically "open Used-by" in the global admin view, or
  // "open Detach confirm" in the app view.
  return (
    <button
      type="button"
      style={{
        ...style,
        cursor: "pointer",
      }}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = meta.border;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = meta.bg;
      }}
    >
      {label}
    </button>
  );
};

export default ResourceScopeBadge;